/**
 * Where a redline lands once its paragraph has been found again: on the
 * paragraph itself, beside it as a new paragraph, or inside it at a phrase.
 *
 * The deterministic locator only ever answers `on_paragraph`; the other kinds
 * come from the core's LLM locate when the original paragraph is gone and the
 * redline text should become a new paragraph under the heading that introduced
 * it, or when the original text now sits inside a longer paragraph. The
 * schema is the wire shape the core returns and the add-in's tracker keeps.
 */
import { z } from "zod";
const insideParagraphPlacementSchemaShape = { kind: z.literal("inside_paragraph"), anchorPhrase: z.string() };

export const redlinePlacementSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("on_paragraph") }),
    z.object({ kind: z.literal("after_paragraph") }),
    z.object({ kind: z.literal("before_paragraph") }),
    z.object(insideParagraphPlacementSchemaShape),
]);

export type RedlinePlacement = z.infer<typeof redlinePlacementSchema>;

export const ON_PARAGRAPH: RedlinePlacement = { kind: "on_paragraph" };

/** True for the two placements that add the redline as a new paragraph and leave the live paragraph untouched. */
export const relocatesBesideParagraph = (placement: RedlinePlacement): boolean =>
    placement.kind === "after_paragraph" || placement.kind === "before_paragraph";
